import { PHYSICS } from '../utils/constants';
import type { VehicleInput, VehicleState } from './vehicle';
import { getNearestBuildingOutwardNormal } from './collision';

function wrapPi(a: number): number {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
}

/**
 * Simple pursuit input — steer toward player, full throttle.
 */
export function computeCopInput(
  cop: VehicleState,
  playerX: number,
  playerZ: number,
): VehicleInput {
  const dx = playerX - cop.x;
  const dz = playerZ - cop.z;
  const targetHeading = Math.atan2(dx, dz);
  const err = wrapPi(targetHeading - cop.heading);
  const steer = Math.max(-1, Math.min(1, err * 3.4));
  return {
    throttle: 1,
    steer,
    handbrake: false,
    nitro: false,
  };
}

/**
 * Smart catch-up boost for police vehicles.
 * 
 * - Applies boost in FORWARD direction only (no sideways movement)
 * - Scales based on distance to player
 * - Reduces boost when close (for orbiting behavior)
 * - Increases boost when far (to catch up)
 */
export function applyCopCatchUp(
  cop: VehicleState,
  dt: number,
  playerX?: number,
  playerZ?: number,
): void {
  if (playerX === undefined || playerZ === undefined) return;

  const near = getNearestBuildingOutwardNormal(cop.x, cop.z);
  if (near && near.dist < 2.5) {
    return;
  }

  const dx = playerX - cop.x;
  const dz = playerZ - cop.z;
  const dist = Math.hypot(dx, dz);
  
  if (dist < 4) return;

  const speed = Math.hypot(cop.vx, cop.vz);
  
  const forwardX = Math.sin(cop.heading);
  const forwardZ = Math.cos(cop.heading);

  const toPlayerX = dx / dist;
  const toPlayerZ = dz / dist;
  const facingPlayer = forwardX * toPlayerX + forwardZ * toPlayerZ;

  if (facingPlayer < 0.05) return;

  const maxSpeed = PHYSICS.MAX_SPEED_MS * 1.22;
  if (speed >= maxSpeed) return;

  let boost: number;
  
  if (dist > 80) {
    boost = 3.6;
  } else if (dist > 60) {
    boost = 2.9;
  } else if (dist > 40) {
    boost = 2.15;
  } else if (dist > 25) {
    boost = 1.35;
  } else if (dist > 15) {
    boost = 0.72;
  } else if (dist > 8) {
    boost = 0.22;
  } else {
    boost = 0.08;
  }

  boost *= facingPlayer;

  const speedRatio = speed / maxSpeed;
  if (speedRatio > 0.8) {
    boost *= (1 - speedRatio) * 5;
  }

  cop.vx += forwardX * boost * dt * 60;
  cop.vz += forwardZ * boost * dt * 60;
}
